/**
 * PortalIntegrations — Pre-built webhook templates for popular tools
 *
 * Shows integration cards for Zapier, Make, HubSpot, Salesforce,
 * Google Sheets, and Slack with expected JSON payloads and setup
 * instructions.
 */

import React, { useState } from 'react';
import PortalLayout from '@/components/portal/PortalLayout';
import { Button } from '@/components/ui/button';
import {
  Plug,
  Copy,
  Check,
  Webhook,
  ArrowRight,
  Zap,
  Globe,
  Users,
  Cloud,
  FileSpreadsheet,
  MessageSquare,
} from 'lucide-react';

// ── Integration Data ─────────────────────────────────────────────────

interface Integration {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  borderColor: string;
  instructions: string[];
  webhookFormat: string;
  payload: Record<string, unknown>;
}

const integrations: Integration[] = [
  {
    id: 'zapier',
    name: 'Zapier',
    description: 'Trigger Zaps from IntentDrive events using the Webhooks by Zapier trigger. Connect to 5,000+ apps automatically.',
    icon: <Zap className="h-6 w-6" />,
    color: 'bg-orange-500/10 text-orange-400',
    borderColor: 'border-orange-500/20 hover:border-orange-500/40',
    instructions: [
      'Create a new Zap and select "Webhooks by Zapier" as the trigger.',
      'Choose "Catch Hook" and copy the webhook URL Zapier gives you.',
      'Paste the URL in your IntentDrive router webhook settings.',
    ],
    webhookFormat: 'https://hooks.zapier.com/hooks/catch/YOUR_ACCOUNT_ID/YOUR_HOOK_ID/',
    payload: {
      event: 'lead.qualified',
      session_id: 'sess_abc123',
      vertical: 'auto_insurance',
      score: 87,
      lead: {
        first_name: 'John',
        last_name: 'Doe',
        email: 'john@example.com',
        phone: '+15551234567',
        zip: '85281',
      },
      metadata: {
        source: 'intentdrive',
        router_id: 'rtr_xyz',
        timestamp: '2026-04-09T12:00:00Z',
      },
    },
  },
  {
    id: 'make',
    name: 'Make (Integromat)',
    description: 'Use the HTTP webhook module to receive IntentDrive events and build powerful multi-step automations.',
    icon: <Globe className="h-6 w-6" />,
    color: 'bg-violet-500/10 text-violet-400',
    borderColor: 'border-violet-500/20 hover:border-violet-500/40',
    instructions: [
      'Create a new scenario and add a "Webhooks" module as the trigger.',
      'Select "Custom webhook" and click "Add" to generate a URL.',
      'Copy the URL and paste it in your IntentDrive router webhook settings.',
    ],
    webhookFormat: 'https://hook.us1.make.com/YOUR_WEBHOOK_ID',
    payload: {
      event: 'lead.qualified',
      session_id: 'sess_abc123',
      vertical: 'auto_insurance',
      score: 87,
      lead: {
        first_name: 'John',
        last_name: 'Doe',
        email: 'john@example.com',
        phone: '+15551234567',
        zip: '85281',
      },
      metadata: {
        source: 'intentdrive',
        router_id: 'rtr_xyz',
        timestamp: '2026-04-09T12:00:00Z',
      },
    },
  },
  {
    id: 'hubspot',
    name: 'HubSpot',
    description: 'Automatically create or update HubSpot contacts when leads are qualified through IntentDrive routers.',
    icon: <Users className="h-6 w-6" />,
    color: 'bg-orange-600/10 text-orange-500',
    borderColor: 'border-orange-600/20 hover:border-orange-600/40',
    instructions: [
      'Go to HubSpot Settings > Integrations > API Key and create a private app token.',
      'Configure your IntentDrive webhook URL as the HubSpot contacts API endpoint.',
      'Map IntentDrive lead fields to HubSpot contact properties.',
    ],
    webhookFormat: 'https://api.hubapi.com/crm/v3/objects/contacts',
    payload: {
      properties: {
        firstname: '{{lead.first_name}}',
        lastname: '{{lead.last_name}}',
        email: '{{lead.email}}',
        phone: '{{lead.phone}}',
        zip: '{{lead.zip}}',
        lifecyclestage: 'lead',
        lead_source: 'IntentDrive',
        hs_lead_status: 'NEW',
      },
    },
  },
  {
    id: 'salesforce',
    name: 'Salesforce',
    description: 'Push qualified leads directly into Salesforce using the Web-to-Lead endpoint. No middleware needed.',
    icon: <Cloud className="h-6 w-6" />,
    color: 'bg-blue-500/10 text-blue-400',
    borderColor: 'border-blue-500/20 hover:border-blue-500/40',
    instructions: [
      'In Salesforce Setup, search "Web-to-Lead" and generate a new form.',
      'Note your OID (organization ID) from the generated HTML.',
      'Configure IntentDrive to POST lead data to the Salesforce endpoint.',
    ],
    webhookFormat: 'https://webto.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8',
    payload: {
      oid: 'YOUR_SALESFORCE_OID',
      first_name: '{{lead.first_name}}',
      last_name: '{{lead.last_name}}',
      email: '{{lead.email}}',
      phone: '{{lead.phone}}',
      zip: '{{lead.zip}}',
      lead_source: 'IntentDrive',
      description: 'Vertical: {{vertical}} | Score: {{score}}',
    },
  },
  {
    id: 'google-sheets',
    name: 'Google Sheets',
    description: 'Log every qualified lead to a Google Sheet via Zapier or Make. Great for reporting and manual review.',
    icon: <FileSpreadsheet className="h-6 w-6" />,
    color: 'bg-green-500/10 text-green-400',
    borderColor: 'border-green-500/20 hover:border-green-500/40',
    instructions: [
      'Create a Zap: Trigger = "Webhooks by Zapier (Catch Hook)" | Action = "Google Sheets (Create Spreadsheet Row)".',
      'Or in Make: Trigger = "Custom Webhook" | Action = "Google Sheets > Add a Row".',
      'Map each lead field to a spreadsheet column.',
    ],
    webhookFormat: 'Via Zapier: https://hooks.zapier.com/hooks/catch/... | Via Make: https://hook.us1.make.com/...',
    payload: {
      timestamp: '{{metadata.timestamp}}',
      session_id: '{{session_id}}',
      vertical: '{{vertical}}',
      score: '{{score}}',
      first_name: '{{lead.first_name}}',
      last_name: '{{lead.last_name}}',
      email: '{{lead.email}}',
      phone: '{{lead.phone}}',
      zip: '{{lead.zip}}',
    },
  },
  {
    id: 'slack',
    name: 'Slack',
    description: 'Get instant Slack notifications when high-value leads are qualified. Never miss a conversion opportunity.',
    icon: <MessageSquare className="h-6 w-6" />,
    color: 'bg-purple-500/10 text-purple-400',
    borderColor: 'border-purple-500/20 hover:border-purple-500/40',
    instructions: [
      'In Slack, go to Apps > Incoming Webhooks and create a new webhook for your channel.',
      'Copy the webhook URL Slack provides.',
      'Paste it in your IntentDrive router webhook settings.',
    ],
    webhookFormat: 'https://hooks.slack.com/services/T00000000/B00000000/XXXXXXXXXXXXXXXXXXXXXXXX',
    payload: {
      text: 'New Qualified Lead from IntentDrive',
      blocks: [
        {
          type: 'section',
          text: {
            type: 'mrkdwn',
            content: '*New Lead* | {{vertical}} | Score: {{score}}\n{{lead.first_name}} {{lead.last_name}} | {{lead.email}} | {{lead.phone}}',
          },
        },
      ],
    },
  },
];

// ── Copy Button Component ────────────────────────────────────────────

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleCopy}
      className="border-slate-700 bg-slate-800/50 hover:bg-slate-700 text-slate-300 hover:text-white text-xs"
    >
      {copied ? (
        <>
          <Check className="h-3.5 w-3.5 mr-1.5 text-emerald-400" />
          Copied!
        </>
      ) : (
        <>
          <Copy className="h-3.5 w-3.5 mr-1.5" />
          {label}
        </>
      )}
    </Button>
  );
}

// ── Integration Card ─────────────────────────────────────────────────

function IntegrationCard({ integration }: { integration: Integration }) {
  const [expanded, setExpanded] = useState(false);
  const payloadStr = JSON.stringify(integration.payload, null, 2);

  return (
    <div
      className={`rounded-xl border ${integration.borderColor} bg-slate-900/50 transition-all duration-200`}
    >
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start gap-4 mb-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${integration.color}`}>
            {integration.icon}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-white">{integration.name}</h3>
            <p className="text-sm text-slate-400 mt-1">{integration.description}</p>
          </div>
        </div>

        {/* Setup Steps */}
        <div className="space-y-2 mb-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Setup</p>
          {integration.instructions.map((step, i) => (
            <div key={i} className="flex items-start gap-2 text-sm text-slate-300">
              <span className="text-emerald-400 font-mono text-xs mt-0.5 shrink-0">{i + 1}.</span>
              <span>{step}</span>
            </div>
          ))}
        </div>

        {/* Webhook URL */}
        <div className="mb-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Webhook URL Format</p>
          <div className="bg-slate-950 rounded-lg p-3 font-mono text-xs text-slate-300 break-all border border-slate-800">
            {integration.webhookFormat}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <CopyButton text={payloadStr} label="Copy Webhook Format" />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            className="text-slate-400 hover:text-white text-xs"
          >
            {expanded ? 'Hide' : 'View'} JSON Payload
            <ArrowRight className={`h-3.5 w-3.5 ml-1 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </Button>
        </div>

        {/* Expandable Payload */}
        {expanded && (
          <div className="mt-4 bg-slate-950 rounded-lg p-4 border border-slate-800 overflow-x-auto">
            <pre className="text-xs text-slate-300 font-mono whitespace-pre">
              {payloadStr}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────

export default function PortalIntegrations() {
  return (
    <PortalLayout>
      <div className="p-6 md:p-8 max-w-5xl mx-auto">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <Plug className="h-5 w-5 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Integrations</h1>
              <p className="text-sm text-slate-400">Connect IntentDrive to your favorite tools with pre-built webhook templates.</p>
            </div>
          </div>
        </div>

        {/* Info Banner */}
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4 mb-8">
          <div className="flex items-start gap-3">
            <Webhook className="h-5 w-5 text-emerald-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm text-white font-medium">How Webhooks Work</p>
              <p className="text-sm text-slate-400 mt-1">
                When a lead is qualified through your router, IntentDrive fires a POST request to your configured webhook URL
                with the lead data as JSON. Use the templates below to connect to your tools.
              </p>
            </div>
          </div>
        </div>

        {/* Integration Cards */}
        <div className="space-y-4">
          {integrations.map((integration) => (
            <IntegrationCard key={integration.id} integration={integration} />
          ))}
        </div>
      </div>
    </PortalLayout>
  );
}
